import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import type { BillLocationValue } from '@/constants/bill-location'
import { BILL_LOCATION_LABELS } from '@/constants/bill-location'
import { useProfileData } from '@/hooks/useProfile'
import { useUpdateUserPreferences } from '@/modules/auth/service/auth'
import {
  normalizeDefaultLocation,
  type OptionalBillLocation,
} from '@/utils/bill-location-preference'

export function useDefaultLocation() {
  const queryClient = useQueryClient()
  const { profile, isLoading } = useProfileData()
  const { mutateAsync: updatePreferences, isLoading: isSaving } = useUpdateUserPreferences()

  const defaultLocation = useMemo(
    () => normalizeDefaultLocation(profile?.default_location),
    [profile?.default_location],
  )

  const setDefaultLocation = useCallback(
    async (location: OptionalBillLocation) => {
      const previous = queryClient.getQueryData<{ data?: { default_location?: string | null } }>([
        'get_auth_me',
      ])

      queryClient.setQueryData(['get_auth_me'], (current: typeof previous) => {
        if (!current?.data) return current
        return {
          ...current,
          data: {
            ...current.data,
            default_location: location || null,
          },
        }
      })

      try {
        await updatePreferences({
          default_location: location || null,
        })
        await queryClient.invalidateQueries({ queryKey: ['get_auth_me'] })
      } catch (error) {
        if (previous) {
          queryClient.setQueryData(['get_auth_me'], previous)
        }
        throw error
      }
    },
    [queryClient, updatePreferences],
  )

  const defaultLocationLabel = defaultLocation
    ? BILL_LOCATION_LABELS[defaultLocation as BillLocationValue]
    : 'ทุกสาขา'

  return {
    defaultLocation,
    defaultLocationLabel,
    isLoading,
    isSaving,
    setDefaultLocation,
  }
}
